import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "@/utils/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, ChevronLeft, ChevronRight, Zap, Ban, Eye, ChevronDown, Shield, Crown } from "lucide-react";
import { toast } from "sonner";

export default function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteConfirmChecked, setDeleteConfirmChecked] = useState(false);

  // Filters and pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [debouncedSearch, roleFilter, sortBy, sortOrder, currentPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get("/admin/users", {
        params: {
          page: currentPage,
          per_page: perPage,
          search: debouncedSearch || undefined,
          role_filter: roleFilter !== "all" ? roleFilter : undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        },
      });

      setUsers(response.data.users);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(users.map((u) => u.user_id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    }
  };

  const handleRowClick = (userId, e) => {
    // Don't navigate if clicking on checkbox
    if (e.target.closest('[role="checkbox"]')) return;
    navigate(`/admin/users/${userId}`);
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error("No users selected");
      return;
    }

    if (action === "delete") {
      setPendingAction("delete");
      setDeleteConfirmChecked(false);
      setShowDeleteDialog(true);
      return;
    }

    // For other actions, execute immediately
    await executeBulkAction(action);
  };

  const executeBulkAction = async (action) => {
    setActionLoading(true);
    try {
      const response = await authAxios.post("/admin/users/bulk-action", {
        user_ids: selectedUsers,
        action: action,
      });

      const actionLabels = {
        delete: "Deleted",
        block: "Blocked",
        unblock: "Unblocked",
        view_only: "Set to View Only",
        remove_view_only: "Removed View Only",
      };

      toast.success(`${actionLabels[action]} ${response.data.affected_count} user(s)`);
      setSelectedUsers([]);
      setShowDeleteDialog(false);
      setPendingAction(null);
      setDeleteConfirmChecked(false);
      fetchUsers();
    } catch (error) {
      console.error("Bulk action failed:", error);
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleGuardianBulk = async (status) => {
    if (!selectedUsers.length) return;
    const reason = window.prompt(
      `Set account_status="${status}" on ${selectedUsers.length} user(s)?\n\nEnter a reason (visible in the Guardian timeline):`,
      "admin bulk action",
    );
    if (reason === null) return;
    setActionLoading(true);
    try {
      const r = await authAxios.post("/admin/fraud/users/bulk-status", {
        user_ids: selectedUsers, status, reason: reason || "admin bulk action",
      });
      toast.success(`Guardian: ${r.data.ok}/${r.data.count} → ${status.toUpperCase()}`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Guardian bulk failed");
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-500/20 text-red-300";
      case "support":
        return "bg-blue-500/20 text-blue-300";
      default:
        return "bg-gray-500/20 text-gray-300";
    }
  };

  const formatTokens = (tokens) => {
    if (!tokens || tokens === 0) return "-";
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
    return tokens.toString();
  };

  return (
    <Card className="bg-divine-charcoal border-divine-gold/20">
      <CardHeader>
        <CardTitle className="text-divine-gold font-cinzel">User Management</CardTitle>
        <CardDescription className="text-gray-400">
          Manage users, roles, and permissions
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-divine-black border-divine-gold/30 text-white"
            />
          </div>
          <Select value={roleFilter} onValueChange={(value) => {
            setRoleFilter(value);
            setCurrentPage(1);
          }}>
            <SelectTrigger className="w-[180px] bg-divine-black border-divine-gold/30 text-white">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent className="bg-divine-charcoal border-divine-gold/30">
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="support">Support</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Select 
            value={`${sortBy}:${sortOrder}`} 
            onValueChange={(value) => {
              const [field, order] = value.split(":");
              setSortBy(field);
              setSortOrder(order);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-[180px] bg-divine-black border-divine-gold/30 text-white">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-divine-charcoal border-divine-gold/30">
              <SelectItem value="created_at:desc">Newest First</SelectItem>
              <SelectItem value="created_at:asc">Oldest First</SelectItem>
              <SelectItem value="name:asc">Name (A-Z)</SelectItem>
              <SelectItem value="name:desc">Name (Z-A)</SelectItem>
              <SelectItem value="role:asc">Role</SelectItem>
              <SelectItem value="total_tokens:desc">Most Tokens</SelectItem>
              <SelectItem value="total_tokens:asc">Least Tokens</SelectItem>
              <SelectItem value="blocked:desc">Blocked First</SelectItem>
              <SelectItem value="view_only:desc">View Only First</SelectItem>
              <SelectItem value="account_status:asc">Guardian Status</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="mb-4 flex items-center gap-3 p-3 bg-divine-gold/10 rounded-lg border border-divine-gold/30">
            <span className="text-sm text-divine-gold">
              {selectedUsers.length} user(s) selected
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
                >
                  Actions <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-divine-charcoal border-divine-gold/30">
                <DropdownMenuItem
                  onClick={() => handleGuardianBulk("active")}
                  className="text-emerald-400 focus:bg-emerald-500/10 focus:text-emerald-300"
                  data-testid="bulk-guardian-active"
                >
                  <Shield className="h-4 w-4 mr-2" /> Set ACTIVE
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleGuardianBulk("warning")}
                  className="text-amber-400 focus:bg-amber-500/10 focus:text-amber-300"
                  data-testid="bulk-guardian-warning"
                >
                  <Shield className="h-4 w-4 mr-2" /> Set WARNING
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleGuardianBulk("frozen")}
                  className="text-red-400 focus:bg-red-500/10 focus:text-red-300"
                  data-testid="bulk-guardian-freeze"
                >
                  <Shield className="h-4 w-4 mr-2" /> FREEZE
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleGuardianBulk("blocked")}
                  className="text-red-300 focus:bg-red-500/10 focus:text-red-200"
                  data-testid="bulk-guardian-block"
                >
                  <Shield className="h-4 w-4 mr-2" /> BLOCK
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkAction("block")}
                  className="text-orange-400 focus:bg-orange-500/10 focus:text-orange-300"
                >
                  <Ban className="h-4 w-4 mr-2" /> Legacy Block flag
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkAction("unblock")}
                  className="text-green-400 focus:bg-green-500/10 focus:text-green-300"
                >
                  <Ban className="h-4 w-4 mr-2" /> Legacy Unblock flag
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkAction("view_only")}
                  className="text-blue-400 focus:bg-blue-500/10 focus:text-blue-300"
                >
                  <Eye className="h-4 w-4 mr-2" /> Set View Only
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkAction("remove_view_only")}
                  className="text-gray-300 focus:bg-gray-500/10 focus:text-gray-200"
                >
                  <Eye className="h-4 w-4 mr-2" /> Remove View Only
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkAction("delete")}
                  className="text-red-400 focus:bg-red-500/10 focus:text-red-300"
                >
                  Delete Users
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-divine-gold border-t-transparent rounded-full"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No users found
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-divine-gold/20 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-divine-gold/20 hover:bg-divine-black/50">
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedUsers.length === users.length && users.length > 0}
                        onCheckedChange={handleSelectAll}
                        className="border-divine-gold/30"
                      />
                    </TableHead>
                    <TableHead className="text-divine-gold">Name</TableHead>
                    <TableHead className="text-divine-gold">Email</TableHead>
                    <TableHead className="text-divine-gold">Role</TableHead>
                    <TableHead className="text-divine-gold">Status</TableHead>
                    <TableHead className="text-divine-gold">
                      <div className="flex items-center gap-1">
                        <Zap className="h-4 w-4" /> Toks
                      </div>
                    </TableHead>
                    <TableHead className="text-divine-gold">
                      <div className="flex items-center gap-1">
                        <Shield className="h-4 w-4" /> Sponsor
                      </div>
                    </TableHead>
                    <TableHead className="text-divine-gold">
                      <div className="flex items-center gap-1">
                        <Crown className="h-4 w-4" /> Leader
                      </div>
                    </TableHead>
                    <TableHead className="text-divine-gold">Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow 
                      key={user.user_id} 
                      className="border-divine-gold/20 hover:bg-divine-black/50 cursor-pointer"
                      onClick={(e) => handleRowClick(user.user_id, e)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedUsers.includes(user.user_id)}
                          onCheckedChange={(checked) => handleSelectUser(user.user_id, checked)}
                          className="border-divine-gold/30"
                        />
                      </TableCell>
                      <TableCell className="font-medium text-white">{user.name}</TableCell>
                      <TableCell className="text-gray-300">{user.email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${getRoleBadgeColor(user.role)}`}>
                          {user.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {user.account_status && user.account_status !== "active" && (
                            <span
                              className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                                user.account_status === "warning"
                                  ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                                  : user.account_status === "frozen"
                                  ? "bg-red-500/15 text-red-300 border-red-500/40"
                                  : "bg-red-600/25 text-red-200 border-red-600/50"
                              }`}
                              data-testid={`user-status-${user.user_id}`}
                            >
                              {user.account_status}
                            </span>
                          )}
                          {user.blocked && (
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-300">
                              <Ban className="h-3 w-3" /> Blocked
                            </span>
                          )}
                          {user.view_only && (
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300">
                              <Eye className="h-3 w-3" /> View Only
                            </span>
                          )}
                          {!user.blocked && !user.view_only &&
                           (!user.account_status || user.account_status === "active") && (
                            <span className="text-gray-500 text-xs">Active</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-300 font-mono text-sm">
                        {formatTokens(user.total_tokens)}
                      </TableCell>
                      <TableCell className="text-gray-300 text-sm">
                        {user.sponsor_name || <span className="text-gray-600">—</span>}
                      </TableCell>
                      <TableCell className="text-gray-300 text-sm">
                        {user.leader_name || <span className="text-gray-600">—</span>}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-gray-400">
                Showing {users.length} of {total} users
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-300">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-divine-charcoal border-divine-gold/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-divine-gold">Delete Users</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Are you sure you want to delete {selectedUsers.length} user(s)? 
              This action cannot be undone and will also delete all associated data 
              (matches, sessions, chat history, etc.).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 py-4">
            <Checkbox
              id="confirm-delete"
              checked={deleteConfirmChecked}
              onCheckedChange={setDeleteConfirmChecked}
              className="border-red-500/50"
            />
            <label htmlFor="confirm-delete" className="text-sm text-red-400">
              I understand this action is permanent and cannot be undone
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
              onClick={() => {
                setShowDeleteDialog(false);
                setPendingAction(null);
                setDeleteConfirmChecked(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => executeBulkAction("delete")}
              disabled={actionLoading || !deleteConfirmChecked}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {actionLoading ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
